import { FilterMatchMode } from 'primereact/api'
import { Card } from 'primereact/card'
import { ColumnProps } from 'primereact/column'
import { useContext, useEffect } from 'react'
import { Member, useMembersOdata } from '../Hooks/useMembers'
import usePayers from '../Hooks/usePayers'
import { SearchStateContext } from '../SearchStateContext'
import DateFilter from './DateFilter'
import StatusFilter from './StatusFilter'
import TablePagination from './TablePagination_Expandable'

const MemberODataSearchTable = (props: {
  selectedMember: Member | undefined
  setSelection: React.Dispatch<React.SetStateAction<Member | undefined>>
}) => {
  const { state, setState, payerIds } = useContext(SearchStateContext)
  const { data: payers, isLoading: isPayersLoading } = usePayers()
  const { data, isLoading } = useMembersOdata(state)

  useEffect(() => {
    if (payers && payerIds) {
      const payerNames = payers.filter((p) => payerIds.includes(p.id)).map((p) => p.name)
      setState((prev) => {
        return {
          ...prev,
          filters: {
            ...prev.filters,
            payerName: { value: payerNames, matchMode: FilterMatchMode.IN },
          },
        }
      })
    }
  }, [payerIds, payers])

  const columns: ColumnProps[] = []
  columns.push({
    field: 'memberId',
    header: 'Health Plan Member ID',
    sortable: true,
    filter: true,
    filterField: 'memberId',
    showFilterMenu: false,
    showClearButton: true,
    filterPlaceholder: '- search -',
    style: { maxWidth: '12rem' },
  })
  columns.push({
    field: 'fullName',
    filterField: 'fullName',
    header: 'Name',
    sortable: true,
    filter: true,
    showFilterMenu: false,
    style: { maxWidth: '8rem' },
    filterPlaceholder: '- search -',
  })
  columns.push({
    field: 'aaneelIdStr',
    filterField: 'aaneelIdStr',
    header: 'WellBe Id',
    sortable: true,
    filter: true,
    showFilterMenu: false,
    style: { maxWidth: '8rem' },
    filterPlaceholder: '- search -',
  })
  columns.push({
    field: 'dtOfBirth',
    header: 'Birth Date',
    sortable: true,
    filter: true,
    filterField: 'dtOfBirth',
    showFilterMenu: false,
    showClearButton: true,
    style: { maxWidth: '8rem' },
    filterPlaceholder: '- search -',
    filterElement: DateFilter,
    body: (rowData: Member) => new Date(rowData.dtOfBirth).toLocaleDateString('en-US', { timeZone: 'UTC' }),
  })
  columns.push({
    field: 'planName',
    header: 'Plan',
    sortable: true,
    filter: true,
    filterField: 'planName',
    showFilterMenu: false,
    showClearButton: true,
    filterPlaceholder: '- search -',
    style: { maxWidth: '12rem' },
  })
  columns.push({
    field: 'payerPortalStatus',
    header: 'Status',
    sortable: true,
    filter: true,
    body: (rowData: Member) => (rowData.termDt != undefined ? 'Termed' : rowData.engaged ? 'Engaged' : 'Eligible/Targeted'),
    filterField: 'payerPortalStatus',
    showFilterMenu: false,
    showClearButton: false,
    style: { maxWidth: '8rem' },
    filterElement: StatusFilter,
  })

  const containsAetna = payerIds?.includes(110007)
  return (
    <Card
      title="Use the search boxes to find a patient"
      subTitle={containsAetna ? '* Remove prefixes (MEN, MEB) for Member Id to improve search' : undefined}
    >
      <TablePagination
        data={data}
        isLoading={isLoading || isPayersLoading}
        setState={setState}
        state={state}
        columns={columns}
        selection={props.selectedMember}
        setSelection={props.setSelection}
        size="small"
      />
    </Card>
  )
}

export default MemberODataSearchTable
